import { useNavigate } from 'react-router-dom';
import { useAccountingSummary } from '@saiens/shared/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, FileEdit, CheckCircle, FileText, Banknote, ChevronRight } from 'lucide-react';

export function AcceptanceOverviewPage() {
  const navigate = useNavigate();
  const { data: summary, isLoading, error } = useAccountingSummary();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const stages = [
    {
      icon: <FileEdit className="h-5 w-5 text-gray-600" />,
      bgColor: 'bg-gray-50',
      title: '草稿/待驗收',
      count: summary?.draft_count ?? 0,
      amount: summary?.draft_amount ?? 0,
      path: '/accounting/overview/draft',
    },
    {
      icon: <CheckCircle className="h-5 w-5 text-green-600" />,
      bgColor: 'bg-green-50',
      title: '已驗收/未開票',
      count: summary?.accepted_count ?? 0,
      amount: summary?.accepted_amount ?? 0,
      path: '/accounting/accepted',
    },
    {
      icon: <FileText className="h-5 w-5 text-blue-600" />,
      bgColor: 'bg-blue-50',
      title: '已開發票',
      count: summary?.invoiced_count ?? 0,
      amount: summary?.invoiced_amount ?? 0,
      path: '/accounting/invoiced',
    },
    {
      icon: <Banknote className="h-5 w-5 text-emerald-600" />,
      bgColor: 'bg-emerald-50',
      title: '已銷賬',
      count: summary?.paid_count ?? 0,
      amount: summary?.paid_amount ?? 0,
      path: '/accounting/paid',
    },
  ];

  return (
    <div className="mx-auto max-w-lg">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate('/accounting')} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">驗收總覽</h1>
      </div>

      {error ? (
        <div className="px-4 py-12 text-center text-red-500">載入失敗</div>
      ) : (
        <div className="space-y-2 px-4 py-4">
          {stages.map((stage) => (
            <button
              key={stage.path}
              onClick={() => navigate(stage.path)}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className={`rounded-lg p-2 ${stage.bgColor}`}>{stage.icon}</div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-sm font-medium text-gray-900">{stage.title}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-gray-900">{stage.count}</p>
                <p className="text-xs text-gray-500">
                  {stage.amount >= 1_000_000
                    ? `$${(stage.amount / 1_000_000).toFixed(1)}M`
                    : stage.amount >= 1_000
                      ? `$${(stage.amount / 1_000).toFixed(0)}K`
                      : `$${stage.amount.toLocaleString()}`}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
